/**
 * One clock for every time shown to a user: the UK wall clock (Europe/London).
 *
 * The data carries two kinds of time value, and both must come out on the same
 * clock:
 *   - **Instants** with a zone (`2026-10-12T10:20:44.000Z`, `…+01:00`) — event
 *     `occurred_at`, `received_at`, `computed_at`. Converted to UK time.
 *   - **UK wall-clock values** with no zone (`2026-10-12T11:20:00`) — Palletways
 *     PodDate/PodTime, PDT ETA windows. Already UK time; shown as written.
 *
 * Never format an instant with `toLocaleString` and no `timeZone`: that uses the
 * viewer's browser zone, so a UTC browser showed a 11:20 BST POD as 10:20 next to
 * a 10:41 window end (order 60050978308).
 */

export const OPERATING_TIME_ZONE = 'Europe/London';

const ZONED = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const WALL = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface ClockParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

const UK_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: OPERATING_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** UK wall-clock fields for an instant or a zone-less UK wall value; `null` if unparseable. */
export function ukClockParts(value: string | Date | null | undefined): ClockParts | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return null;
    if (!ZONED.test(s)) {
      const m = s.match(WALL);
      if (!m) return null;
      return { year: m[1], month: m[2], day: m[3], hour: m[4], minute: m[5], second: m[6] ?? '00' };
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const p: Record<string, string> = {};
  for (const part of UK_PARTS.formatToParts(d)) p[part.type] = part.value;
  return { year: p.year, month: p.month, day: p.day, hour: p.hour, minute: p.minute, second: p.second };
}

/** `12 Oct, 11:20` */
export function formatUkDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  const p = ukClockParts(value);
  if (!p) return fallback;
  return `${p.day} ${MONTHS[Number(p.month) - 1]}, ${p.hour}:${p.minute}`;
}

/** `12/10 11:20` — the compact form used in the orders tables. */
export function formatUkShortDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  const p = ukClockParts(value);
  if (!p) return fallback;
  return `${p.day}/${p.month} ${p.hour}:${p.minute}`;
}

/** `11:20`, or `11:20:44` with `seconds`. */
export function formatUkTime(
  value: string | Date | null | undefined,
  opts: { seconds?: boolean } = {},
  fallback = '—',
): string {
  const p = ukClockParts(value);
  if (!p) return fallback;
  return opts.seconds ? `${p.hour}:${p.minute}:${p.second}` : `${p.hour}:${p.minute}`;
}

/** `12 Oct` */
export function formatUkDate(value: string | Date | null | undefined, fallback = '—'): string {
  const p = ukClockParts(value);
  if (!p) return fallback;
  return `${p.day} ${MONTHS[Number(p.month) - 1]}`;
}

/** `12 Oct 2026, 11:20` */
export function formatUkFullDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  const p = ukClockParts(value);
  if (!p) return fallback;
  return `${p.day} ${MONTHS[Number(p.month) - 1]} ${p.year}, ${p.hour}:${p.minute}`;
}

/** `12 Oct 2026` */
export function formatUkFullDate(value: string | Date | null | undefined, fallback = '—'): string {
  const p = ukClockParts(value);
  if (!p) return fallback;
  return `${p.day} ${MONTHS[Number(p.month) - 1]} ${p.year}`;
}

/**
 * "Data current as of": `11:20:44` on today's UK date, `12 Oct 11:20:44` on an
 * earlier one, so data that stopped yesterday does not read as current.
 */
export function formatUkAsOf(
  value: string | Date | null | undefined,
  now: Date = new Date(),
  fallback = '',
): string {
  const p = ukClockParts(value);
  const today = ukClockParts(now);
  if (!p || !today) return fallback;
  const time = `${p.hour}:${p.minute}:${p.second}`;
  const sameDay = p.year === today.year && p.month === today.month && p.day === today.day;
  return sameDay ? time : `${p.day} ${MONTHS[Number(p.month) - 1]} ${time}`;
}
